import { type FC, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import SearchRounded from '@mui/icons-material/SearchRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';

// common
import { LINKING_STATUS } from 'common/constants';
import type { MonthOption } from 'common/utils/schoolYearMonths';
import { Client } from 'modules/account/types';

interface Props {
  clients: Client[];
  selectedClient: Client | null;
  onSelectClient: (c: Client | null) => void;
  /** `YYYY-MM` của tháng đang chọn; rỗng khi chưa tải xong danh sách tháng. */
  monthKey: string;
  onChangeMonth: (monthKey: string) => void;
  /** 12 tháng của năm học — thay cho 12 tháng của năm dương lịch. */
  monthOptions: MonthOption[];
  nccStatus: string;
  onChangeNccStatus: (s: string) => void;
  onSearch: () => void;
  onReset: () => void;
  /** Khóa dropdown chọn trường (dùng cho khách hàng / phụ huynh — trường cố định theo tài khoản). */
  disableClientSelect?: boolean;
}

/** Một ô lọc: nhãn nằm trên + control bên dưới, để cả hàng thẳng hàng khi căn đáy. */
const Field: FC<{ label: string; children: ReactNode; required?: boolean; sx?: object }> = ({
  label,
  children,
  required,
  sx,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ...sx }}>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
      {required && <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>}
    </Typography>
    {children}
  </Box>
);

const FIELD_HEIGHT = 40;

const DashboardFilter: FC<Props> = ({
  clients,
  selectedClient,
  onSelectClient,
  monthKey,
  onChangeMonth,
  monthOptions,
  nccStatus,
  onChangeNccStatus,
  onSearch,
  onReset,
  disableClientSelect,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
      <Field
        label={formatMessage({ id: 'dashboard.filter.customer' })}
        required
        sx={{ flex: 1, minWidth: 240 }}
      >
        <Autocomplete<Client>
          size="small"
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { height: FIELD_HEIGHT, py: 0 } }}
          options={clients}
          value={selectedClient}
          disabled={disableClientSelect}
          getOptionLabel={(o) => o?.name || o?.operationCode || ''}
          isOptionEqualToValue={(o, v) => o?.operationCode === v?.operationCode}
          onChange={(_, v) => onSelectClient(v)}
          renderInput={(p) => (
            <TextField {...p} placeholder={formatMessage({ id: 'dashboard.filter.customer' })} />
          )}
        />
      </Field>

      <Field label={formatMessage({ id: 'dashboard.filter.nccStatus' })} required sx={{ minWidth: 180 }}>
        <TextField
          select
          size="small"
          fullWidth
          disabled
          value={nccStatus}
          onChange={(e) => onChangeNccStatus(e.target.value)}
        >
          {LINKING_STATUS.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {formatMessage({ id: s.name })}
            </MenuItem>
          ))}
        </TextField>
      </Field>

      {/*
        12 tháng của năm học (T9/N → T8/N+1) thay cho 12 tháng của năm dương lịch. Value là
        `YYYY-MM` nên năm luôn đi kèm tháng.
      */}
      <Field label={formatMessage({ id: 'dashboard.filter.month' })} required sx={{ minWidth: 180 }}>
        <TextField
          select
          size="small"
          fullWidth
          value={monthOptions.some((o) => o.id === monthKey) ? monthKey : ''}
          onChange={(e) => onChangeMonth(e.target.value)}
        >
          {monthOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>
      </Field>

      <Button
        variant="contained"
        startIcon={<SearchRounded />}
        onClick={onSearch}
        disableElevation
        sx={{
          height: FIELD_HEIGHT,
          px: 3,
          bgcolor: '#F26B21',
          textTransform: 'none',
          '&:hover': { bgcolor: '#E05E18' },
        }}
      >
        {formatMessage({ id: 'search' })}
      </Button>
      <Button
        variant="outlined"
        onClick={onReset}
        sx={{
          height: FIELD_HEIGHT,
          minWidth: FIELD_HEIGHT,
          px: 1,
          color: '#F26B21',
          borderColor: '#F3B48E',
          '&:hover': { borderColor: '#F26B21', bgcolor: '#FEF3EC' },
        }}
      >
        <RefreshRounded />
      </Button>
    </Box>
  );
};

export default DashboardFilter;
